import {
  type InboxEntry,
  type InboxEntryAgentDelegation,
  NON_TERMINAL_AGENT_DELEGATION_STATUSES,
  type TicketArtifact,
} from '@this+that/models';

import { isUserVisibleDelegation } from './delegationStatusPresentation';

const RUN_KEY_SEPARATOR = '#';

export interface TicketRun {
  delegation: InboxEntryAgentDelegation;
  /** The id the panel opens the run by: `delegationId`, else the legacy `runId`, else the entry id. */
  runId: string;
  /** Unique per run across the app: `entryId#delegationId`, or the bare entry id for a legacy run. */
  runKey: string;
  /** Key of the run's status override in `useDoBoxRunViewStore`; the primary keeps its entry id. */
  overrideKey: string;
  isPrimary: boolean;
}

type ServedCollection<T> =
  | readonly T[]
  | Readonly<Record<string, T>>
  | null
  | undefined;

/** GraphQL serves these as lists (`null` when absent), the socket as lists (`undefined` when
 *  absent) and storage as a map by id; every reader treats all of them alike. */
export function toServedList<T>(value: ServedCollection<T>): T[] {
  if (value == null) {
    return [];
  }
  return Array.isArray(value)
    ? [...(value as readonly T[])]
    : Object.values(value as Readonly<Record<string, T>>);
}

/** The wire carries `agentRuns` as a list while the shared model types the stored map; this is
 *  the one place the list is written back onto an entry. */
export function withAgentRuns(
  entry: InboxEntry,
  runs: InboxEntryAgentDelegation[],
): InboxEntry {
  return { ...entry, agentRuns: runs as unknown as InboxEntry['agentRuns'] };
}

export function buildRunKey(
  entryId: string,
  delegationId: string | null | undefined,
): string {
  return delegationId
    ? `${entryId}${RUN_KEY_SEPARATOR}${delegationId}`
    : entryId;
}

export function resolveRunId(
  entryId: string,
  delegation: Pick<InboxEntryAgentDelegation, 'delegationId' | 'runId'>,
): string {
  return delegation.delegationId ?? delegation.runId ?? entryId;
}

export function isRunActive(
  delegation: Pick<InboxEntryAgentDelegation, 'status'>,
): boolean {
  return NON_TERMINAL_AGENT_DELEGATION_STATUSES.has(delegation.status);
}

function buildRun(
  entryId: string,
  delegation: InboxEntryAgentDelegation,
  isPrimary: boolean,
): TicketRun {
  const runKey = buildRunKey(entryId, delegation.delegationId);
  return {
    delegation,
    runId: resolveRunId(entryId, delegation),
    runKey,
    overrideKey: isPrimary ? entryId : runKey,
    isPrimary,
  };
}

function compareNewestStartedFirst(
  a: InboxEntryAgentDelegation,
  b: InboxEntryAgentDelegation,
): number {
  return (b.startedAt ?? 0) - (a.startedAt ?? 0);
}

/** The ticket's live runs: the primary first, then the secondary ones newest first, deduped by
 *  `delegationId`. The one place that reads `agentDelegation` and `agentRuns` together. */
export function listTicketRuns(
  entry: Pick<InboxEntry, 'id' | 'agentDelegation' | 'agentRuns'>,
): TicketRun[] {
  const runs: TicketRun[] = [];
  const seenDelegationIds = new Set<string>();
  const primary = entry.agentDelegation;
  if (primary) {
    runs.push(buildRun(entry.id, primary, true));
    if (primary.delegationId) {
      seenDelegationIds.add(primary.delegationId);
    }
  }
  const secondary = toServedList(entry.agentRuns).sort(
    compareNewestStartedFirst,
  );
  for (const delegation of secondary) {
    if (delegation.delegationId) {
      if (seenDelegationIds.has(delegation.delegationId)) {
        continue;
      }
      seenDelegationIds.add(delegation.delegationId);
    }
    runs.push(buildRun(entry.id, delegation, false));
  }
  return runs;
}

export function listVisibleTicketRuns(
  entry: Pick<InboxEntry, 'id' | 'agentDelegation' | 'agentRuns'>,
): TicketRun[] {
  return listTicketRuns(entry).filter((run) =>
    isUserVisibleDelegation(run.delegation),
  );
}

export function findTicketRun(
  entry: Pick<InboxEntry, 'id' | 'agentDelegation' | 'agentRuns'>,
  runId: string | undefined,
): TicketRun | undefined {
  if (runId === undefined) {
    return undefined;
  }
  return listTicketRuns(entry).find((run) => run.runId === runId);
}

/** The delegation id to send with a mutation: absent for a legacy run that never had one, so
 *  the API keeps acting on the primary exactly as before. */
export function toDelegationIdArg(
  delegation:
    | Pick<InboxEntryAgentDelegation, 'delegationId'>
    | null
    | undefined,
): string | undefined {
  return delegation?.delegationId ?? undefined;
}

export function listTicketArtifacts(
  entry: Pick<InboxEntry, 'ticketArtifacts'>,
): TicketArtifact[] {
  return toServedList<TicketArtifact>(entry.ticketArtifacts).filter(
    (artifact) => artifact.pullRequest != null,
  );
}

/** The key a run's local UI state lives under (composer drafts, published status): the primary
 *  keeps its entry id, so what is already stored for it stays reachable, and only a run found in
 *  the secondary list gets its own key. */
export function resolveRunStoreKey(
  entry: Pick<InboxEntry, 'id' | 'agentRuns'>,
  delegation: Pick<InboxEntryAgentDelegation, 'delegationId'>,
): string {
  const { delegationId } = delegation;
  if (!delegationId) {
    return entry.id;
  }
  const isSecondary = toServedList(entry.agentRuns).some(
    (run) => run.delegationId === delegationId,
  );
  return isSecondary ? buildRunKey(entry.id, delegationId) : entry.id;
}

/** The run whose executor ledger card this is: cards in the assistant conversation carry no
 *  delegation id, only the ledger item they sit on. */
export function findRunByLedgerRef(
  entry: Pick<InboxEntry, 'id' | 'agentDelegation' | 'agentRuns'>,
  ref: { conversationId: string; runId: string; msgIdx: string },
): TicketRun | undefined {
  return listTicketRuns(entry).find(({ delegation }) => {
    const candidate = delegation.ledgerRef;
    return (
      candidate?.conversationId === ref.conversationId &&
      candidate.runId === ref.runId &&
      candidate.msgIdx === ref.msgIdx
    );
  });
}

/** The run `after` holds that `before` did not: the one a "delegate" call just created, whichever
 *  slot it landed in. Newest first when several appeared. */
export function findNewRun(
  before: Pick<InboxEntry, 'id' | 'agentDelegation' | 'agentRuns'> | undefined,
  after: Pick<InboxEntry, 'id' | 'agentDelegation' | 'agentRuns'>,
): TicketRun | undefined {
  const knownRunIds = new Set(
    before ? listTicketRuns(before).map((run) => run.runId) : [],
  );
  return listTicketRuns(after)
    .filter((run) => !knownRunIds.has(run.runId))
    .sort((a, b) => compareNewestStartedFirst(a.delegation, b.delegation))[0];
}
